'use client';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSidebar } from '@/contexts/SidebarContext';
import Toast from './Toast';

const ToastContext = createContext(null);

export function useToast() {
    return useContext(ToastContext);
}

let idCounter = 1;

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const [_queue, setQueue] = useState([]);
    const [lastChange, setLastChange] = useState(null); // 'added' | 'removed' | null
    const focusRef = useRef(true);
    const [topOffset, setTopOffset] = useState(null);
    const roRef = useRef(null);
    const sidebar = useSidebar(); // Optional - will be null if SidebarProvider not available

    // Calculate offset directly from sidebar state - react immediately to closing animation
    const sidebarOffset =
        sidebar?.active === 'submit' && !sidebar?.isClosing ? 536 : 0; // 520px + 16px padding

    useEffect(() => {
        function onFocus() {
            focusRef.current = true;
        }
        function onBlur() {
            focusRef.current = false;
        }
        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    // Dynamic navbar positioning
    useEffect(() => {
        const navbar = document.getElementById('navbar');
        const GAP = 8;

        const updateOffset = () => {
            const h = navbar?.offsetHeight || 0;
            setTopOffset(h > 0 ? h + GAP : null);
        };

        updateOffset();

        if (navbar && typeof ResizeObserver !== 'undefined') {
            roRef.current = new ResizeObserver(updateOffset);
            roRef.current.observe(navbar);
        }

        window.addEventListener('resize', updateOffset);

        return () => {
            if (roRef.current) roRef.current.disconnect();
            window.removeEventListener('resize', updateOffset);
        };
    }, []);

    const MAX_TOASTS = 6; // visible stack limit
    const DEDUP_WINDOW = 6000; // ms - ignore identical toasts within this window
    const recentKeysRef = useRef(new Map()); // Map<string, expiryMs>

    const push = useCallback(
        ({ title, description, duration = 5000, variant = 'info' }) => {
            const now = Date.now();
            // clean expired recent keys
            try {
                const m = recentKeysRef.current;
                for (const [k, exp] of m.entries()) {
                    if (exp <= now) m.delete(k);
                }
            } catch (_e) {
                /* best-effort */
            }

            const key = `${String(title || '')}|${String(description || '')}`;
            // If we've recently shown this exact toast, ignore to avoid duplicates
            if (recentKeysRef.current.has(key)) {
                return null;
            }

            // record the key for dedup window
            try {
                recentKeysRef.current.set(key, now + DEDUP_WINDOW);
            } catch (_e) {}

            const id = idCounter++;
            const entry = {
                id,
                title,
                description,
                duration,
                variant,
                created: now,
            };
            setLastChange('added');
            setToasts((cur) => {
                // append new toasts; index 0 is oldest
                if (cur.length < MAX_TOASTS) return [...cur, entry];
                // queue overflow (FIFO)
                setQueue((q) => [...q, entry]);
                return cur;
            });
            return id;
        },
        [],
    );

    const remove = useCallback((id) => {
        setLastChange('removed');
        // Use functional updates to atomically promote from queue and avoid race conditions
        setToasts((prevToasts) => {
            const next = prevToasts.filter((x) => x.id !== id);
            let promoted = null;

            setQueue((prevQueue) => {
                if (prevQueue.length === 0 || next.length >= MAX_TOASTS)
                    return prevQueue;
                // take the first queued item (FIFO)
                promoted = prevQueue[0];
                return prevQueue.slice(1);
            });

            if (promoted) {
                // append promoted toast to the end (newer)
                return [...next, promoted];
            }
            return next;
        });
    }, []);

    // Expose global fallback for helper functions to work without explicit context
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.__ps_add_toast = push;
        }
        return () => {
            if (
                typeof window !== 'undefined' &&
                window.__ps_add_toast === push
            ) {
                delete window.__ps_add_toast;
            }
        };
    }, [push]);

    const contextValue = useMemo(
        () => ({
            push, // existing method (keep for compatibility)
            remove, // existing method (keep for compatibility)
            addToast: push, // new alias
            removeToast: remove, // new alias
        }),
        [push, remove],
    );

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <div
                className="fixed right-0 z-50 pointer-events-none"
                style={{
                    top: `${topOffset ?? 88}px`,
                    paddingRight: '1rem',
                    transform: `translateX(-${sidebarOffset}px)`,
                    transition: 'transform 300ms ease-in-out',
                }}
            >
                <div className="relative">
                    {/* Overflow indicator */}
                    {_queue.length > 0 && (
                        <div className="mb-2 flex justify-end">
                            <div className="px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm text-sm text-gray-600 pointer-events-auto">
                                +{_queue.length} more
                            </div>
                        </div>
                    )}
                    <div className="relative">
                        {toasts.map((toast, index) => {
                            const n = toasts.length;
                            const baseGap = 22; // spacing between stacked toasts
                            const peek = 12; // how much of the next toast peeks out
                            const maxStacked = 6; // visible area uses normal spacing for first N
                            const overflowGap = 6; // compressed spacing for overflow

                            // index 0 is oldest (top). compute vertical offset so tops remain visible.
                            let top;
                            if (index < maxStacked) {
                                top = index * (baseGap - peek);
                            } else {
                                const anchor =
                                    (maxStacked - 1) * (baseGap - peek);
                                top =
                                    anchor +
                                    (index - (maxStacked - 1)) * overflowGap;
                            }

                            const z = 10000 + (n - index);
                            const wrapperStyle = {
                                zIndex: z,
                                top: `${top}px`,
                                right: 0,
                                transition:
                                    lastChange === 'removed'
                                        ? 'top 260ms cubic-bezier(0.2,0.8,0.2,1)'
                                        : 'none',
                            };

                            return (
                                <div
                                    key={toast.id}
                                    className="absolute right-0"
                                    style={wrapperStyle}
                                >
                                    <Toast
                                        toast={toast}
                                        index={index}
                                        initialIndex={index}
                                        initialTotal={n}
                                        active={index === 0}
                                        total={n}
                                        onDone={() => remove(toast.id)}
                                        focusRef={focusRef}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </ToastContext.Provider>
    );
}

export default ToastProvider;
